import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

import countViewedNotifications from '~/components/main-navbar/notifications/count-viewed-notifications.ts';
import { getUserNotificationsSorted } from '~/database/supabase/notifications/queries.server.ts';
import type { CleanNotificationWithRelations } from '~/database/supabase/notifications/types.ts';
import { getUserPortfoliosForLayout } from '~/database/supabase/portfolio/queries.server.ts';
import type { PortfolioForLayoutType } from '~/database/supabase/portfolio/types.ts';
import { getLatestUserSearches, getSearchSuggestions } from '~/database/supabase/user_search/queries.server.ts';
import type { SearchSuggestion, UserSearch } from '~/database/supabase/user_search/types.ts';
import { getUserWatchlistsForLayout } from '~/database/supabase/watchlist/queries.server.ts';
import type { WatchlistForLayoutType } from '~/database/supabase/watchlist/types.ts';
import { toPublicAuthSession } from '~/modules/auth/mappers.ts';
import type { PublicAuthSession } from '~/modules/auth/types.ts';
import { isAuthenticatedAppPath } from '~/routing/authenticated-app-paths.ts';
import { getHints } from '~/utils/client-hints.tsx';
import { getEnv } from '~/utils/env.server.ts';
import { getTheme } from '~/utils/theme-session.server.ts';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { getAuthSession } from '../auth/session.server.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';

export interface AppShellData {
	readonly authSession: PublicAuthSession | null;
	readonly env: ReturnType<typeof getEnv>;
	readonly latestSearches: UserSearch[] | null;
	readonly notificationCount: number;
	readonly notifications: CleanNotificationWithRelations[] | null;
	readonly portfolios: PortfolioForLayoutType[] | null;
	readonly requestInfo: {
		readonly hints: ReturnType<typeof getHints>;
		readonly userPrefs: {
			readonly theme: ReturnType<typeof getTheme>;
		};
	};
	readonly searchSuggestions: SearchSuggestion[] | null;
	readonly watchlists: WatchlistForLayoutType[] | null;
}

export const getAppShellData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: [
				'auth.session',
				'supabase.notifications.sorted',
				'supabase.portfolio.layout',
				'supabase.watchlist.layout',
				'supabase.search-suggestions',
				'supabase.user-search.latest',
			],
			relatesTo: 'app.shell',
		}),
	])
	.handler(async () => {
		const request = getRequest();
		const pathname = new URL(request.url).pathname;
		const authSession = isAuthenticatedAppPath(pathname)
			? await requireStartAuthSession({ request })
			: await getAuthSession(request);
		const env = getEnv();
		const requestInfo = {
			hints: getHints(request),
			userPrefs: {
				theme: getTheme(request),
			},
		} satisfies AppShellData['requestInfo'];

		if (!authSession) {
			return {
				authSession: null,
				env,
				latestSearches: null,
				notificationCount: 0,
				notifications: null,
				portfolios: null,
				requestInfo,
				searchSuggestions: null,
				watchlists: null,
			} satisfies AppShellData;
		}

		const [notifications, portfolios, watchlists, searchSuggestions, latestSearches] = await Promise.all([
			getUserNotificationsSorted(authSession.userId, true, true),
			getUserPortfoliosForLayout(authSession.userId),
			getUserWatchlistsForLayout(authSession.userId),
			getSearchSuggestions(),
			getLatestUserSearches(authSession.userId),
		]);

		return {
			authSession: toPublicAuthSession(authSession),
			env,
			latestSearches,
			notificationCount: countViewedNotifications(notifications),
			notifications,
			portfolios,
			requestInfo,
			searchSuggestions,
			watchlists,
		} satisfies AppShellData;
	});
